import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom'
import { FocusStyleManager, Spinner } from '@blueprintjs/core'
import { observer } from 'mobx-react-lite'

import InstantNavbar from './components/layout/InstantNavbar'
import { Sidebar } from './components/layout/Sidebar'
import MindmapInterface from './components/Mindmap/MindmapInterface'
// import { themeStore } from './stores/ThemeStore' // Now accessed via useAppStore
import { OnboardingScreen } from './components/onboarding'
import UserProfile from './onboarding/UserProfile'
import GithubIntegrationLanding from './components/integrations/GithubIntegrationLanding'
import GithubOAuthCallback from './components/integrations/GithubOAuthCallback'
import SettingsPage from './components/settings/SettingsPage'
import Dashboard from './components/Dashboard/Dashboard'
import ChatInterface from './components/chat/ChatInterface'
import ChangelogPage from './components/changelog/ChangelogPage'
import NodeDocPage from './pages/NodeDocPage'
import StytchDiscoveryAuth from './components/auth/StytchDiscoveryAuth'
import { StytchSessionSync } from './components/auth/StytchSessionSync'
import UnifiedAppInitializer from './components/UnifiedAppInitializer'
import { useStytchMemberSession } from '@stytch/react/b2b'
// import { rootStore, useRootStore } from './stores/UnifiedStoreProvider' // OLD
import { useAppStore } from './stores/UnifiedStoreProvider' // NEW
// import BackendLogsViewer from './components/BackendLogsViewer/BackendLogsViewer' // Moved to Settings page
import { analyticsService } from './services/analytics.service'
import { PerfectCursors } from './components/collaboration/PerfectCursors'
import { featureFlags } from './featureFlags'
// import StoreDebugPanel from './components/dev/StoreDebugPanel'

// Import Blueprint CSS styles
import '@blueprintjs/core/lib/css/blueprint.css'
import '@blueprintjs/icons/lib/css/blueprint-icons.css'
import '@blueprintjs/popover2/lib/css/blueprint-popover2.css'
import './App.css'
import './styles/blueprint-enhanced.css'
import './styles/gpu-performance.css'
import './styles/dark-mode-blueprint.css'
import './styles/comprehensive-dark-mode.css'

// Initialize Blueprint.js focus style manager
FocusStyleManager.onlyShowFocusOnTabs()

/**
 * Main App component  
 * Uses WebSocket + Stytch for auth and data, MobX for layout and theme
 */
const App: React.FC = observer(() => {
  // ALL HOOKS MUST BE AT THE TOP - BEFORE ANY CONDITIONAL RETURNS
  const appStore = useAppStore();
  const [showDebug, setShowDebug] = useState(false);
  
  // Prevent console clearing for debugging
  useEffect(() => {
    if (typeof console.clear === 'function') {
      const originalClear = console.clear;
      console.clear = () => {
        console.warn('⚠️ Console.clear() was called but blocked for debugging');
        console.trace('Console clear stack trace:');
      };
    }
  }, []);
  
  // Keyboard shortcut handler
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault()
        setShowDebug((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, []);
  
  // Add navigation listener to detect unexpected navigations
  useEffect(() => {
    const handleNavigation = (e: PopStateEvent) => {
      console.warn('🔄 PopState event detected:', {
        state: e.state,
        currentUrl: window.location.href,
        type: e.type
      });
    };
    
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      console.warn('⚠️ BeforeUnload event - page is about to refresh/close!');
      console.trace('BeforeUnload stack trace');
    };
    
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Initialize PostHog analytics
  useEffect(() => {
    analyticsService.initialize();
  }, []);
  
  // Track user identification when authenticated (only if store exists)
  useEffect(() => {
    if (appStore && appStore.auth.isReallyAuthenticated && appStore.auth.user) {
      analyticsService.identify(appStore.auth.user.id, {
        email: appStore.auth.user.email,
        organizationId: appStore.user.currentOrganization?.id,
        organizationName: appStore.user.currentOrganization?.name,
        role: appStore.auth.user?.role,
      });
      
      // Track login event
      analyticsService.trackEvent('user_login', {
        method: 'stytch',
        organization: appStore.user.currentOrganization?.slug,
      });
    } else if (appStore && !appStore.auth.isReallyAuthenticated && !appStore.auth.isLoading) {
      analyticsService.reset();
    }
  }, [appStore]);
  
  // Load org users when org is available
  useEffect(() => {
    console.log('🔍 [App.useEffect] Org users check:', {
      hasAppStore: !!appStore,
      hasCurrentOrg: !!appStore?.user.currentOrganization,
      currentOrgId: appStore?.user.currentOrganizationId,
      orgSlug: appStore?.user.currentOrganization?.slug,
      usersCount: appStore?.user.users.length
    });
    
    if (appStore && appStore.user.currentOrganization && appStore.user.currentOrganizationId) {
      console.log('✅ [App] Triggering loadOrgUsers for:', appStore.user.currentOrganization.slug);
      appStore.user.loadOrgUsers();
    } else {
      console.warn('⚠️ [App] Cannot load org users - missing requirements');
    }
  }, [appStore, appStore?.user.currentOrganization, appStore?.user.currentOrganizationId]);

  // ON PUBLIC ROUTES - render minimal router without store
  if (!appStore) {
    console.log('🎬 [App] Rendering on public route (no store)');
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<StytchDiscoveryAuth />} />
          <Route path="/auth/callback" element={<StytchDiscoveryAuth />} />
          {/* Legacy routes redirect to login */}
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/auth/accept-invite" element={<Navigate to="/auth/callback" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }
  
  // AUTHENTICATED - destructure store
  const { auth, user, theme } = appStore;
  const { isLoading, isReallyAuthenticated } = auth;
  
  console.log('🎬 [App] Rendering with auth state:', {
    isReallyAuthenticated,
    isLoading,
    user: auth.user?.email,
    pathname: window.location.pathname
  });

  if (isLoading) {
    return (
      <div className={`app-container ${theme.isDark ? 'bp5-dark' : ''}`} data-theme={theme.isDark ? 'dark' : 'light'}>
        <InstantNavbar />
        <div className="app-loading">
          <p>Loading...</p>
        </div>
      </div>
    )
  }

    return (
      <Router>
        <StytchSessionSync />
        <UnifiedAppInitializer>
          <div className={`app-container ${theme.isDark ? 'bp5-dark' : ''}`} data-theme={theme.isDark ? 'dark' : 'light'}>
            {/* Global collaborative cursors */}
            {featureFlags.ENABLE_COLLABORATIVE_CURSORS && isReallyAuthenticated && (
              <PerfectCursors roomType="project" roomId="global" />
            )}
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<StytchDiscoveryAuth />} />
              <Route path="/auth/callback" element={<StytchDiscoveryAuth />} />
              {/* Legacy routes redirect to login/callback */}
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/auth/accept-invite" element={<Navigate to="/auth/callback" replace />} />
              <Route path="/admin/*" element={<Navigate to="/login" replace />} />
              <Route path="/test-org/*" element={<Navigate to="/login" replace />} />
              <Route path="/prod-org/*" element={<Navigate to="/login" replace />} />
              <Route path="/:orgSlug/mindmap" element={<AuthenticatedRoute><MindmapInterface /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/mindmap/:mindmapId" element={<AuthenticatedRoute><MindmapInterface /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/mindmap/:mindmapId/node/:nodeId/doc" element={<AuthenticatedRoute><NodeDocPage /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/chat" element={<AuthenticatedRoute><ChatInterface /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/chat/:chatId" element={<AuthenticatedRoute><ChatInterface /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/dashboard" element={<AuthenticatedRoute><Dashboard /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/settings" element={<AuthenticatedRoute><SettingsPage /></AuthenticatedRoute>} />
              <Route path="/:orgSlug/changelog" element={<AuthenticatedRoute><ChangelogPage /></AuthenticatedRoute>} />
              <Route path="/integrations/github" element={<GithubIntegrationLanding />} />
              <Route path="/github/oauth/callback" element={<GithubOAuthCallback />} />
              <Route path="/onboarding" element={<AuthenticatedRoute><UserProfile /></AuthenticatedRoute>} />
            </Routes>
          </div>
          {/* <StoreDebugPanel visible={showDebug} onClose={() => setShowDebug(false)} /> */}
        </UnifiedAppInitializer>
      </Router>
  );
});

const RootRedirect: React.FC = () => {
  const appStore = useAppStore();
  
  // If no store (shouldn't happen but be safe), redirect to login
  if (!appStore) {
    return <Navigate to="/login" replace />;
  }
  
  const { auth, preferences } = appStore;
  const isReallyAuthenticated = auth.isReallyAuthenticated;
  
  console.log('[RootRedirect] Auth state:', {
    isReallyAuthenticated,
    isAuthenticated: auth.isAuthenticated,
    lastAuthCheckFailed: auth.lastAuthCheckFailed,
    accessToken: !!auth.accessToken,
    user: !!auth.user
  });
  
  if (isReallyAuthenticated) {
    const orgSlug = auth.currentOrg?.slug || auth.organization?.slug;
    console.log('[RootRedirect] Authenticated, orgSlug:', orgSlug);
    if (orgSlug) {
      // Check for saved mindmap preference first
      if (preferences.selectedMindmapId) {
        console.log('[RootRedirect] Redirecting to saved mindmap:', preferences.selectedMindmapId);
        return <Navigate to={`/${orgSlug}/mindmap/${preferences.selectedMindmapId}`} replace />;
      }
      // Check for saved chat preference
      if (preferences.selectedChatId) {
        console.log('[RootRedirect] Redirecting to saved chat:', preferences.selectedChatId);
        return <Navigate to={`/${orgSlug}/chat/${preferences.selectedChatId}`} replace />;
      }
      // Default to mindmap view
      return <Navigate to={`/${orgSlug}/mindmap`} replace />;
    }
    // If no org, redirect to login
    return <Navigate to="/login" replace />;
  }
  
  console.log('[RootRedirect] Not authenticated, redirecting to login');
  return <Navigate to="/login" replace />;
};


const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const appStore = useAppStore();
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'failed'>('checking');
  
  const { session } = useStytchMemberSession();
  const auth = appStore?.auth;
  
  // Determine current view based on path
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.includes('/mindmap')) return 'mindmap';
    if (path.includes('/chat')) return 'chat';
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/changelog')) return 'changelog';
    return 'projects';
  };
  
  const handleNavigate = (view: string) => {
    const orgSlug = location.pathname.split('/')[1];
    if (orgSlug) {
      switch(view) {
        case 'mindmap':
          navigate(`/${orgSlug}/mindmap`);
          break;
        case 'chat':
          navigate(`/${orgSlug}/chat`);
          break;
        case 'dashboard':
          navigate(`/${orgSlug}/dashboard`);
          break;
        case 'settings':
          navigate(`/${orgSlug}/settings`);
          break;
        case 'changelog':
          navigate(`/${orgSlug}/changelog`);
          break;
        default:
          navigate(`/${orgSlug}/dashboard`);
      }
    }
  };
  
  // SINGLE SOURCE OF TRUTH: Auth check with timeout and cleanup
  useEffect(() => {
    // Check 1: No Stytch session → Failed immediately
    if (!session || !session.organization_id) {
      console.log('[AuthenticatedRoute] No Stytch session → FAILED');
      setAuthState('failed');
      return;
    }
    
    // Check 2: Already authenticated → Success immediately
    if (auth?.isAuthenticated) {
      console.log('[AuthenticatedRoute] Already authenticated → SUCCESS');
      setAuthState('authenticated');
      sessionStorage.removeItem('auth_redirect_count');
      sessionStorage.removeItem('auth_loop_detected');
      return;
    }
    
    // Check 3: Waiting for auth sync
    console.log('[AuthenticatedRoute] Waiting for StytchSessionSync to complete...');
    setAuthState('checking');
    
    // TIMEOUT: DISABLED for debugging - was clearing sessions too aggressively
    const timeoutId = setTimeout(async () => {
      console.warn('⏰ [AuthenticatedRoute] Auth timeout reached after 60s');
      console.warn('   Auth state:', auth?.isAuthenticated);
      console.warn('   Session exists:', !!session);
      // Don't clear session - just log for debugging
    }, 60000);
    
    return () => clearTimeout(timeoutId);
  }, [session, auth?.isAuthenticated]);
  
  // RENDER: Simple binary decision based on authState
  
  if (authState === 'failed') {
    return <Navigate to="/login" replace />;
  }
  
  if (authState === 'checking') {
    // ONE loading screen while waiting for auth sync
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#ffffff'
      }}>
        <Spinner size={50} />
        <p style={{ marginTop: '20px', fontSize: '16px' }}>Loading application...</p>
        <p style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
          This should take less than 2 seconds
        </p>
      </div>
    );
  }
  
  // authState === 'authenticated' - render the app!

  return (
    <>
      <InstantNavbar />
      <div className="app-layout">
          <Sidebar 
          currentView={getCurrentView() as any} 
          onNavigate={handleNavigate} 
        />
        <main className="app-content">
          {children}
        </main>
      </div>
    </>
  );
});

export default App;
